import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthHandler } from '@usimweb/core-auth';
import { ApiService } from '@usimweb/core/services';
import { Comments, Measurement, Results, Versions } from '@usimweb/core/models';
import * as fromCeetron from '@usimweb/ceetron';
import * as fromStore from '@usimweb/state/store';
import * as fromRoot from '@usimweb/state/router-store';

import { Store } from '@ngrx/store';
import { Observable, skip, take } from 'rxjs';
import { DialogService, RequestSupportDialogComponent, VersionDialogComponent } from '@usimweb/ui/dialogs';
import { MatDialog } from '@angular/material/dialog';
import { ProcessDialogComponent } from 'apps/foaming/src/app/dialogs/process-dialog/process-dialog.component';

@Component({
    selector: 'app-result',
    templateUrl: './result.component.html',
    styleUrls: ['./result.component.scss'],
    standalone: false
})
export class ResultComponent implements OnInit, AfterViewInit, OnDestroy {
  private uuid: string = '';
  private project_uuid: string = '';
  private versions!: Versions[];

  public comments$!: Observable<Comments[]>;
  public results$!: Observable<Results>;
  public loadViewer = false;

  public selectedResult = 'density';
  public densityMapScalar = 'Density';
  public isOwner = false;
  public user = AuthHandler.userName || '';
  public ids = Object();
  public dialogService_!: DialogService;
  public measurements: Measurement[] = [];

  public isFailed: boolean = true;

  public get animationTaskAlive() {
    return this.animationService.isAnimationTaskAlive;
  }

  public get animationBarOpen() {
    return this.modelService.animationBarOpen;
  }

  // @ViewChild('viewer') viewer!: CeetronViewerComponent;

  constructor(
    private c3Service: fromCeetron.C3Service,
    private modelService: fromCeetron.ModelService,
    private animationService: fromCeetron.AnimationService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<fromStore.DashboardState>,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private apiService: ApiService
  ) {
    this.dialogService_ = this.dialogService;
  }

  ngOnInit(): void {
    this.project_uuid = this.route.snapshot.paramMap.get('id')?.toString() || '';
    this.uuid = this.route.snapshot.paramMap.get('id2')?.toString() || '';
    this.ids = { project: this.project_uuid, version: this.uuid };
    this.loadViewer = true;
    this.getProject();
    this.getVersions();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.animationService.isAnimationTaskAlive = false;
    this.modelService.clearTask();
    this.store.dispatch(new fromStore.ClearResult());
  }

  back() {
    this.store.dispatch(new fromRoot.Go({ path: ['/app/versions/' + this.project_uuid] }));
  }

  private getProject() {
    this.store
      .select(fromStore.getProjectById(this.project_uuid))
      .pipe(skip(1))
      .subscribe((project) => {
        if (AuthHandler.basfUserName.toLocaleLowerCase() === project?.created_by) {
          this.isOwner = true;
        }
      });
  }

  private getVersions() {
    this.store
      .select(fromStore.getAllVersions)
      .pipe(skip(1), take(1))
      .subscribe((versions) => {
        this.versions = versions;
        const version = versions.find((x) => x.uuid === this.uuid);
        if (version && version.status.overall !== 'failed') {
          this.isFailed = false;
          this.getResultCurves();
          this.getRTData(version);
          this.loadModel();
          return;
        }
        this.openErrorDialog();
      });
  }

  private openErrorDialog() {
    this.dialogService
      .openContentDialog(ProcessDialogComponent, { projectStatus: 'version_failed' }, '')
      .subscribe((result) => {
        if (result === 'support') {
          this.requestSupportDialog();
        }
        if (result === 'restart') {
          this.refreshResults();
        }
        if (!result) {
          this.back();
        }
      });
  }

  private loadModel() {
    this.store.dispatch(new fromStore.LoadToken(this.uuid));
    setTimeout(() => {
      let width = window.screen.width / 2.4;
      if (window.screen.width < 1200) {
        width = window.screen.width * 0.9;
      }
      this.c3Service._viewer['0'].resizeViewer(width, 550);
      this.openModel();
    }, 1000);
  }

  private getResult() {
    this.results$ = this.store.select(fromStore.getResult);
  }

  private openModel(): void {
    if (this.modelService.modelOpen) {
      this.modelService.taskPool[0].alive = true;
    }
    this.getToken();
  }

  private getToken() {
    this.store
      .select(fromStore.getToken)
      .pipe(take(1))
      .subscribe((token) => {
        this.modelService.openModel(token as any, '0', this.animationService, true);
        this.modelService.scalarResult.subscribe((scalar) => {
          if (scalar) {
            this.densityMapScalar = 'Density [node]';
            this.setDensity();
          }
        });
      });
  }

  private deleteVersion(version: Versions) {
    this.store.dispatch(new fromStore.DeleteVersion(version));
  }

  private getRTData(version: Versions) {
    const matdata = version?.matdata;
    if (matdata) {
      const subscription = this.apiService.getRTData(matdata).subscribe((curves) => {
        this.measurements = curves;
        subscription.unsubscribe();
      });
    }
  }

  public getResultCurves(type?: string) {
    this.store.dispatch(new fromStore.LoadCurves({ uuid: this.uuid, filling: type || 'filling' }));
    this.results$ = this.store.select(fromStore.getResult);
  }

  public downloadPpx() {
    // results.foaming[0].title
    this.store.dispatch(new fromStore.LoadPpx({ uuid: this.uuid, name: 'presentation' }));
  }

  public refreshResults() {
    this.store.dispatch(new fromStore.RetryVersion(this.uuid));
    this.back();
  }

  public setDensity() {
    this.modelService.setSMapScalar(this.densityMapScalar);
    this.selectedResult = 'density';
  }

  public setRT() {
    this.modelService.setSMapScalar('RT');
    this.selectedResult = 'rt';
  }

  public setPressure() {
    this.modelService.setSMapScalar('Pressure');
    this.selectedResult = 'pressure';
    // const settings = this.c3Service._model[0].getVectorSettingsAt(0);
    console.log('set nodeAveragedValues  !!!');
    // settings.nodeAveragedValues(true);

    const settingsArr = this.c3Service._model['0'].modelDirectory.scalarResultArray;

    console.log(settingsArr);

    const settings = this.c3Service._model['0'].getVectorSettingsArray();

    console.log('settings');
    console.log(settings);

    const pressureSettings = settings[3];

    console.log('is settings');
    console.log(pressureSettings);

    const nodeAverage = pressureSettings.nodeAveragedValues;

    console.log('is node average');
    console.log(nodeAverage);

    pressureSettings.nodeAveragedValues = true;
  }

  public setShearStress() {
    this.modelService.setSMapScalar('shearStress');
    this.selectedResult = 'shearStress';
  }

  public startAnimation() {
    this.animationService.play(true);
  }

  public stopAnimation() {
    this.animationService.stop();
  }

  public openAnalyzer() {
    // this.viewer.ngOnDestroy();
    setTimeout(() => {
      this.router.navigate(['app/viewer/' + this.uuid + '/' + this.project_uuid]);
    });
  }

  openVersionsDialog() {
    const version = this.versions.find((x) => x.uuid === this.uuid);
    const projectData = {
      versions: this.versions,
      currentVersion: version,
    };
    const dialogRef = this.dialog.open(VersionDialogComponent, {
      data: projectData,
      disableClose: true,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result.action === 'loadversion' && this.uuid !== result.version.uuid) {
        this.store.dispatch(new fromStore.LoadResult({ uuid: result.version.uuid, filling: 'filling' }));
        this.store.dispatch(new fromStore.LoadToken(result.version.uuid));
        this.getResult();
        this.openModel();
        // this.version = result.version;
        // this.router.navigateByUrl(`/app/viewer/${this.projectId}/${this.version.version}`);
      } else if (result.action === 'deleteversion') {
        this.deleteVersion(result.version);
        this.router.navigateByUrl('/app/versions/' + this.project_uuid);
      } else if (result.action === 'compareVersions') {
        const selectedVersions = result.selectedVersions;

        let compareVersions: any = [];
        selectedVersions.forEach((version: number) => {
          const uuid = this.versions.find((x: Versions, i: number) => x.version === version)?.uuid;
          if (uuid) {
            compareVersions.push(uuid);
          }
        });
        compareVersions = compareVersions.join('/');
        this.router.navigateByUrl('/app/compare/' + this.project_uuid + '/' + compareVersions);
      }
    });
  }

  requestSupportDialog() {
    this.dialogService
      .openContentDialog(RequestSupportDialogComponent, { uuid: this.project_uuid, projects: null }, 'Request support')
      .subscribe(() => {
        this.openErrorDialog();
      });
  }
}
